import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Marker, Polyline } from '@react-google-maps/api';
import { Map } from '../../../ui/core';
import { laneColorFor, REVERSE_LANE_COLOR } from '../laneGeometry';
import AreaSelect, { bboxToBoundsLiteral, seedBboxFromMap } from '../../Stations/StationBuilder/AreaSelect';
import { runDisplayPath } from './boxSelection';

// Same fallback center LaneMap.js uses when there is nothing better to show
// (here: before the lane's own bounds are fit).
const DEFAULT_CENTER = { lat: 30.2672, lng: -97.7431 };
const DEFAULT_ZOOM = 8;

const FROZEN_OPTIONS = {
  strokeColor: '#9e9e9e',
  strokeOpacity: 0.35,
  strokeWeight: 3,
};

const highlightOptions = (color) => ({
  strokeColor: color,
  strokeOpacity: 0.95,
  strokeWeight: 4,
  zIndex: 5,
});

// Contrasting colour for the snapped candidate, drawn over the (now dimmed)
// original selection so the operator can compare the two directly.
const CANDIDATE_OPTIONS = {
  strokeColor: '#e91e63',
  strokeOpacity: 0.95,
  strokeWeight: 4,
  zIndex: 6,
};

/**
 * A short line segment about the origin, used as the repeated "tick" that
 * makes a dashed Polyline. Google Maps' `Polyline` has no native dash-array;
 * the documented technique is a near-zero `strokeOpacity` on the line itself
 * plus a symbol (this path) repeated along it via `icons`. A path literal,
 * not `window.google.maps.SymbolPath` — see the `WAYPOINT_PIN_PATH` comment
 * below for why that distinction matters at module scope.
 */
const DASH_TICK_PATH = 'M 0,-1 0,1';

/**
 * The reverse lane, drawn only in REVIEW (see the header comment) whenever
 * `preview.reverseLane` exists. Dashed AND a colour fixed apart from the
 * palette `laneColorFor` hashes from — the brief for this is a production
 * safety gap: `lane_description` is null for almost every lane, so the
 * geometry itself, not the panel's text, is what tells the operator which
 * of up to 4 same-corridor routes is about to be written. A colour alone
 * would not survive a monochrome bug-report screenshot; the dash does.
 *
 * `REVERSE_LANE_COLOR` is shared with `ReviewPanel.js` (see `laneGeometry.js`)
 * so the panel's reference to "the reverse lane" and this line can never
 * silently drift apart into two different colours.
 */
const REVERSE_CURRENT_OPTIONS = {
  strokeColor: REVERSE_LANE_COLOR,
  strokeOpacity: 0,
  strokeWeight: 3,
  icons: [{ icon: { path: DASH_TICK_PATH, strokeOpacity: 0.6, scale: 3 }, offset: '0', repeat: '16px' }],
  zIndex: 3,
};

// The reverse lane's re-snapped candidate, drawn over `REVERSE_CURRENT_OPTIONS`
// the same way the primary candidate is drawn over the frozen lane: same
// colour (so the panel link holds), heavier weight and a denser dash so the
// two are still tellable apart on the map itself.
const REVERSE_CANDIDATE_OPTIONS = {
  strokeColor: REVERSE_LANE_COLOR,
  strokeOpacity: 0,
  strokeWeight: 5,
  icons: [{ icon: { path: DASH_TICK_PATH, strokeOpacity: 1, scale: 4 }, offset: '0', repeat: '20px' }],
  zIndex: 7,
};

/** A circle of radius 6 about the origin. A path literal — see below. */
export const WAYPOINT_PIN_PATH = 'M 0,0 m -6,0 a 6,6 0 1,0 12,0 a 6,6 0 1,0 -12,0';

/**
 * WHY A PATH LITERAL AND NOT `SymbolPath.CIRCLE`.
 *
 * This previously read `window.google?.maps?.SymbolPath?.CIRCLE` at MODULE
 * scope — evaluated when `pages/Location/index.js` imports the editor, long
 * before `useJsApiLoader` resolves. The optional chain keeps the module from
 * throwing under test, where `window.google` is absent, but it also turns
 * "Maps has not loaded yet" into `path: undefined`, which Google silently
 * treats as "no icon given" and replaces with its default red teardrop. Frozen
 * wrong at import time, with no error anywhere: waypoints would simply not
 * look like waypoints.
 *
 * This is the exact failure documented at length on
 * `StationBuilder/index.js`'s `EXISTING_STATION_ICON`, and the same one as the
 * `DrawingManager` removal earlier on this branch — reading a Maps global
 * defensively converts a load-order problem into a silently degraded UI. A
 * path literal has no `window.google` dependency, so there is no load order to
 * get wrong, and it stays a frozen constant with stable identity so `<Marker>`
 * does not rebuild every pin on every render.
 */
const WAYPOINT_ICON = Object.freeze({
  path: WAYPOINT_PIN_PATH,
  scale: 1,
  fillColor: '#e91e63',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2,
});

/**
 * A square about the origin — see `WAYPOINT_PIN_PATH` above for why this is a
 * path literal rather than a `window.google.maps.SymbolPath` constant.
 *
 * Deliberately a different SHAPE from `WAYPOINT_PIN_PATH`'s circle, not just
 * a different colour. This marker is the lane's actual endpoint — dragging it
 * changes which physical point the lane starts or ends at, which is a
 * different class of edit from a waypoint nudging the routed corridor between
 * two fixed anchors. Colour alone would not survive a monochrome
 * bug-report screenshot; the shape does.
 */
export const TERMINUS_HANDLE_PATH = 'M 0,-9 9,0 0,9 -9,0 Z';

/**
 * Where a waypoint actually landed after Mapbox snapped it to a road.
 *
 * A hollow ring rather than a filled pin, and deliberately not draggable: it
 * is not a control, it is a REPORT. Dragging it would suggest the operator
 * can place a point on a road directly, which is exactly what they cannot do
 * — they move their own waypoint and the router decides again.
 *
 * Same colour as the waypoint it belongs to so the pairing reads without a
 * connecting line, which at these zoom levels would be a few pixels long and
 * add clutter for nothing.
 */
const SNAPPED_WAYPOINT_ICON = Object.freeze({
  path: WAYPOINT_PIN_PATH,
  scale: 1,
  fillColor: '#ffffff',
  fillOpacity: 0.9,
  strokeColor: '#e91e63',
  strokeWeight: 2,
});

const TERMINUS_HANDLE_ICON = Object.freeze({
  path: TERMINUS_HANDLE_PATH,
  scale: 1,
  fillColor: '#ff9800',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2,
});

/**
 * The map half of the editor: the frozen lane, the highlighted run(s), and
 * the rectangle the operator drags to pick a stretch to correct.
 *
 * USES `AreaSelect`, NOT `DrawingManager`. `DrawingManager` was removed in
 * Maps JavaScript API 3.65 — constructing one throws and takes the page
 * blank, which is exactly what happened here once already (see the header
 * comment on `AreaSelect.js` and the `LIBRARIES` comment in `ui/core/Map.js`).
 * `AreaSelect` is a core, editable `Rectangle` the operator drags and resizes
 * instead of drawing from scratch.
 *
 * `AreaSelect`'s bbox shape (`{ swLng, swLat, neLng, neLat }`) is NOT what
 * `boxSelection.js`'s `findRuns`/`isInsideBox` consume
 * (`{ north, south, east, west }`). The conversion happens right here, via
 * `bboxToBoundsLiteral`, so neither `boxSelection.js` nor the editor shell
 * (`index.js`) has to know `AreaSelect` exists — the shell only ever sees the
 * `{ north, south, east, west }` shape through `onBoxDrawn`.
 *
 * WAYPOINT PLACEMENT VS. THE RECTANGLE'S OWN GESTURES. `AreaSelect`'s
 * `Rectangle` is `clickable`/`draggable`/`editable` — a click or drag that
 * lands on the shape is consumed by the overlay and never reaches the map's
 * own `onClick`, so a naive "click the map to drop a waypoint" handler would
 * be unreachable everywhere the rectangle sits, which is exactly where the
 * selected run (and thus the waypoints for it) lives. Once the editor leaves
 * SELECT (`canDrawBox` goes false), the rectangle has done its job and stops
 * being interactive: `AreaSelect` is rendered `disabled`, which flips the
 * `Rectangle` to
 * `editable: false, draggable: false, clickable: false` (see AreaSelect.js).
 * With the shape no longer clickable, its clicks pass through to the map, so
 * `Map`'s `onClick` fires there. Before a selection exists there are no
 * waypoints to place anyway, so nothing is lost by leaving the rectangle live
 * for dragging/resizing during SELECT.
 */
const EditorMap = ({
  lane,
  path,
  selection,
  runCandidates = [],
  onBoxDrawn,
  waypoints = [],
  // The same waypoints in the order the snap will VISIT them — computed once
  // by the shell so the map's numbers and the routed sequence cannot drift.
  // Falls back to `waypoints` so a caller that omits it still renders.
  routedWaypoints = null,
  // `{ position, snappedPoint, name }[]` — where the last snap actually put
  // each waypoint. Empty until a snap has run.
  snappedWaypointRows = [],
  onAddWaypoint,
  onMoveWaypoint,
  onRemoveWaypoint,
  candidatePath = null,
  canDrawBox = true,
  // The editor's current stage ('select' | 'adjust' | 'review' — see STAGE
  // in index.js). The reverse lane is drawn only during REVIEW: during
  // SELECT and ADJUST the operator is working the primary lane, and a
  // second full-length line across the map would only make the rectangle
  // and waypoints harder to work with.
  stage = 'select',
  // `preview.reverseLane`'s current geometry, already resolved through
  // `geoJsonToPath` by `index.js` — this component never parses GeoJSON
  // itself. `[]` (not null) whenever there is no reverse twin or no
  // geometry to show, so this stays inert without a null check at every
  // call site.
  reverseLanePath = [],
  // The reverse lane's re-snapped candidate, once that re-snap reaches
  // `status: 'ready'`. Also `[]` until then.
  reverseCandidatePath = [],
  // Where the operator has dragged the start/end terminus handle so far —
  // `null` until dragged, in which case the handle sits at the lane's own
  // current terminus (`path[0]` / `path[path.length - 1]`). Lifted up to
  // `index.js` rather than held locally: the snap request needs this
  // position too, not just the map.
  startTerminus = null,
  endTerminus = null,
  // `(which, point)` with `which` one of 'start' | 'end'. Fired on drag end,
  // same as the waypoint markers below.
  onMoveTerminus,
}) => {
  const [map, setMap] = useState(null);
  const [bbox, setBbox] = useState(null);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Fit the frozen lane on first load, then seed a rectangle from that view
  // so the operator starts with something to drag rather than an empty map.
  useEffect(() => {
    if (!map || !path.length || bbox) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
    const seed = seedBboxFromMap(map);
    if (seed) setBbox(seed);
    // Runs once per map/path pairing; re-running on every `bbox` change would
    // fight the operator's own drags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, path]);

  // `AreaSelect` sends `null` when the operator clears the rectangle, and that
  // has to reach the parent. Swallowing it (the old `if (literal)`) left the
  // rectangle gone from the map while the selection it produced stayed live —
  // including any "Section 1 / Section 2" buttons, which remained clickable
  // and would snap and save a stretch of a box the operator had just cleared.
  const handleAreaChange = useCallback(
    (next) => {
      setBbox(next);
      onBoxDrawn(bboxToBoundsLiteral(next));
    },
    [onBoxDrawn],
  );

  const color = laneColorFor(lane.id);

  // Gated on `stage === 'adjust'`, not merely on `selection` existing — the
  // same rule, and the same reason, as the waypoint markers below.
  // `selection` survives into REVIEW, so a `selection`-only guard meant every
  // click on the map while reviewing silently appended a waypoint. The
  // markers are hidden in REVIEW, so they accumulated invisibly: they no
  // longer described the geometry under review, they were sent in the save's
  // edit context, and a subsequent re-snap (of this lane or the reverse one)
  // routed through points the operator never knowingly placed.
  const onMapClick = useCallback(
    (event) => {
      if (stage !== 'adjust' || !selection || !onAddWaypoint) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      onAddWaypoint(point);
    },
    [stage, selection, onAddWaypoint],
  );

  // Highlight the confirmed selection once one exists; otherwise highlight
  // every candidate run so the operator can see what each numbered choice
  // refers to.
  const highlightedRuns = useMemo(
    () => (selection ? [selection.run] : runCandidates),
    [selection, runCandidates],
  );

  return (
    <Map
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      onLoad={onLoad}
      onClick={onMapClick}
      mapContainerStyle={{ width: '100%', height: '100%' }}
    >
      <Polyline path={path} options={FROZEN_OPTIONS} />

      {/* `runDisplayPath`, not a raw slice: a run over a segment the box
          crosses contains no vertices at all, and slicing it directly yields
          an empty array — nothing highlighted, on a stretch about to be
          re-routed. */}
      {highlightedRuns.map((run) => (
        <Polyline
          key={`${run.startIndex}-${run.endIndex}`}
          path={runDisplayPath(path, run)}
          options={highlightOptions(color)}
        />
      ))}

      {candidatePath && <Polyline path={candidatePath} options={CANDIDATE_OPTIONS} />}

      {/* The reverse lane. Gated on `stage === 'review'` on top of the empty-
          array defaults above: `preview` (and so these paths) is only ever
          populated once the editor reaches REVIEW, but the stage check is
          the actual requirement from the brief, not an incidental consequence
          of when `index.js` happens to compute them. */}
      {stage === 'review' && reverseLanePath.length > 0 && (
        <Polyline path={reverseLanePath} options={REVERSE_CURRENT_OPTIONS} />
      )}

      {stage === 'review' && reverseCandidatePath.length > 0 && (
        <Polyline path={reverseCandidatePath} options={REVERSE_CANDIDATE_OPTIONS} />
      )}

      {/* The terminus handle(s) and the waypoint markers below are both
          gated on `stage === 'adjust'`, not merely on `selection` existing.
          `selection` persists through REVIEW (nothing clears it on a
          successful snap), so a `selection &&` check alone would leave both
          still draggable there — a stray drag would mutate `startTerminus`/
          `endTerminus` or `waypoints` without touching the candidate already
          under review, silently desynchronising the map from the geometry
          the operator is about to write. Gating on the stage closes that for
          both; the terminus handle is new in this task, the waypoint gate is
          a pre-existing gap being closed alongside it. */}

      {/* The terminus handle(s). Rendered whenever the confirmed selection's
          run reaches an end of the lane (`widenRun`'s `movesStart`/
          `movesEnd`) — there is no anchor vertex on that side, so the new
          endpoint has to come from the operator dragging this marker rather
          than from existing geometry. Distinct icon from `WAYPOINT_ICON` (see
          `TERMINUS_HANDLE_PATH` above): this is the lane's own endpoint, not
          a point along the re-routed section. */}
      {stage === 'adjust' && selection?.anchors?.movesStart && path.length > 0 && (
        <Marker
          position={startTerminus ?? path[0]}
          draggable
          icon={TERMINUS_HANDLE_ICON}
          onDragEnd={(event) =>
            onMoveTerminus?.('start', { lat: event.latLng.lat(), lng: event.latLng.lng() })
          }
        />
      )}

      {stage === 'adjust' && selection?.anchors?.movesEnd && path.length > 0 && (
        <Marker
          position={endTerminus ?? path[path.length - 1]}
          draggable
          icon={TERMINUS_HANDLE_ICON}
          onDragEnd={(event) =>
            onMoveTerminus?.('end', { lat: event.latLng.lat(), lng: event.latLng.lng() })
          }
        />
      )}

      {/* Numbered by the order the route will actually VISIT them, which is
          their order along the corridor — not the order they were clicked.
          `orderWaypointsAlong` decides the routed sequence, so the label has
          to come from the same function or the map would show one order while
          the snap used another. Before this, every waypoint was an identical
          unlabelled circle: the operator had no way to see the sequence they
          were imposing, which is what made a weaving snap look like the
          editor inventing geometry rather than following instructions. */}
      {stage === 'adjust' &&
        selection &&
        waypoints.map((point, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Marker
            key={index}
            position={point}
            draggable
            icon={WAYPOINT_ICON}
            label={{
              text: String((routedWaypoints ?? waypoints).indexOf(point) + 1),
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
            onDragEnd={(event) =>
              onMoveWaypoint?.(index, { lat: event.latLng.lat(), lng: event.latLng.lng() })
            }
            onClick={() => onRemoveWaypoint?.(index)}
          />
        ))}

      {/* Where those waypoints actually landed. Same stage gate as the
          waypoints themselves — in REVIEW the operator is reading a station
          diff, not adjusting a route, and these would only add noise. */}
      {stage === 'adjust' &&
        selection &&
        snappedWaypointRows
          // No marker for a waypoint whose landing place is unknown. The row
          // still lists it — the road name and "snap distance unknown" are
          // real information — but there is no honest place to draw it.
          .filter((row) => row.snappedPoint)
          .map((row) => (
          <Marker
            key={`snapped-${row.key}`}
            position={row.snappedPoint}
            icon={SNAPPED_WAYPOINT_ICON}
            title={row.name || 'Unnamed road'}
            label={{
              text: String(row.position),
              color: '#e91e63',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
          />
        ))}

      {/* Disabled outside the SELECT stage so the rectangle stops consuming
          clicks/drags — see the header note. Driven by the stage
          (`canDrawBox`) rather than by `!!selection`: a revert reaches REVIEW
          with no selection at all, and a live rectangle there would let a
          stray drag jump back to ADJUST and silently abandon the reverted
          candidate under review. */}
      <AreaSelect bbox={bbox} onChange={handleAreaChange} map={map} disabled={!canDrawBox} />
    </Map>
  );
};

export default EditorMap;
